/**
 * Soft I1a: the pot shown on the INPUT screen only (sticky strip), in bb.
 *
 * It is `deriveHandFlow(hand).potBb` — blinds plus the chips the entered
 * actions put in — but only when every part of that sum is an entered fact.
 * If any part would be a stand-in, the pot is unknown (UI shows 「?」):
 *  - a bet/raise with no bb size, or a size not above the bet it faces
 *    (handFlow then uses a placeholder level),
 *  - an all-in with neither a known stack nor a size,
 *  - an ante (handFlow does not add antes),
 *  - blinds that are not numbers, or an action the flow could not place,
 *  - a setup pot (`hand.potBb`) that differs from this sum — the screen never
 *    shows a second, different pot number (equal → the number is fine).
 *    Screen-only reconciliation: handFlow and the share card stay as they are.
 * Zero estimation: no %, odds, SPR or guess. Never part of the share card,
 * Copy, PNG or `#/h/` — those keep their own (unchanged) text.
 */
import { actorSeat, deriveHandFlow, parseBb } from '../lib/handFlow'
import type { HandState } from '../lib/types'

export type InputPot = { kind: 'known'; bb: number } | { kind: 'unknown' }

function stackKnown(hand: HandState, seat: string): boolean {
  if (seat === hand.heroPosition && Number.isFinite(hand.heroStackBb) && hand.heroStackBb > 0) return true
  return hand.effectiveStackBb != null && Number.isFinite(hand.effectiveStackBb) && hand.effectiveStackBb > 0
}

export function inputPot(hand: HandState): InputPot {
  const unknown: InputPot = { kind: 'unknown' }
  if (!(Number.isFinite(hand.smallBlind) && Number.isFinite(hand.bigBlind) && hand.bigBlind > 0 && hand.smallBlind >= 0)) {
    return unknown
  }
  if (hand.ante != null && Number.isFinite(hand.ante) && hand.ante > 0) return unknown
  for (let i = 0; i < hand.actions.length; i++) {
    const a = hand.actions[i]!
    const before = deriveHandFlow({ ...hand, actions: hand.actions.slice(0, i) })
    const seat = actorSeat(a.actor, hand)
    const opts = before.street === a.street ? before.optionsFor(seat) : null
    if (!opts) return unknown
    if (a.type === 'bet' || a.type === 'raise') {
      const size = parseBb(a.size)
      if (size == null || !(size > opts.currentBet)) return unknown
    } else if (a.type === 'allin') {
      if (!stackKnown(hand, seat) && parseBb(a.size) == null) return unknown
    }
  }
  const pot = deriveHandFlow(hand).potBb
  if (!Number.isFinite(pot) || pot <= 0) return unknown
  if (hand.potBb != null && Number.isFinite(hand.potBb) && hand.potBb > 0 && Math.abs(hand.potBb - pot) > 0.005) {
    return unknown
  }
  return { kind: 'known', bb: pot }
}
